import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Hero } from '../components/Hero';
import { Problem } from '../components/Problem';
import { Approach } from '../components/Approach';
import { Proven } from '../components/Proven';
import { Difference } from '../components/Difference';
import { OneFoundation } from '../components/OneFoundation';
import { useLanguage } from '../components/LanguageContext';
import { Footer } from '../components/Footer';
import { trackEvent as track } from '../utils/analytics';
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
// Each tile links to its real industry page. The invitation tile routes to the demo request.
const INDUSTRIES = [{
  title: 'Cities & Government',
  titleFr: 'Villes & Gouvernement',
  desc: 'Connect citizens, communications and services.',
  descFr: 'Connectez les citoyens, les communications et les services.',
  to: '/industries/cities-government'
}, {
  title: 'Airports & Transportation',
  titleFr: 'Aéroports & Transport',
  desc: 'Create trusted traveler experiences.',
  descFr: 'Créez des expériences de voyage de confiance.',
  to: '/industries/airports'
}, {
  title: 'Universities & Campuses',
  titleFr: 'Universités & Campus',
  desc: 'Unify student identity and campus services.',
  descFr: "Unifiez l'identité étudiante et les services du campus.",
  to: '/industries/universities'
}, {
  title: 'Health Networks',
  titleFr: 'Réseaux de Santé',
  desc: 'Coordinate identity, consent and services.',
  descFr: "Coordonnez l'identité, le consentement et les services.",
  to: '/industries/health'
}, {
  title: 'Telecom & Media',
  titleFr: 'Télécom & Médias',
  desc: 'Turn subscribers into engaged communities.',
  descFr: 'Transformez les abonnés en communautés engagées.',
  to: '/industries/telecom'
}, {
  title: "Don't see your sector?",
  titleFr: 'Vous ne voyez pas votre secteur ?',
  desc: 'The same architecture adapts to any community.',
  descFr: "La même architecture s'adapte à n'importe quelle collectivité.",
  to: '/request-demo',
  isInvitation: true
}];
export function Home() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  useEffect(() => {
    track('Page Viewed', {
      page: 'Home'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-2249">
      <Header data-id="element-2250" />

      <main data-id="element-2251">
        {/* SECTION 1: HERO */}
        <Hero data-id="element-2252" />

        {/* SECTION 2: THE PROBLEM — capacity reframe */}
        <Problem data-id="element-2253" />

        {/* SECTION 3: OUR APPROACH — the connected flow + ATNA layer */}
        <Approach data-id="element-2254" />

        {/* SECTION 4: PROVEN IN PRODUCTION */}
        <Proven data-id="element-2255" />

        {/* SECTION 5: THE DIFFERENCE — verified identity + verification */}
        <Difference data-id="element-2256" />

        {/* SECTION 6: ONE FOUNDATION — modules on one ATNA core */}
        <OneFoundation data-id="element-2257" />

        {/* SECTION 7: ONE PLATFORM. MANY COMMUNITIES. */}
        <section id="communities" className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2258">
          <div className="max-w-6xl mx-auto" data-id="element-2259">
            <div className="text-center mb-20" data-id="element-2260">
              <motion.div {...reveal} className="font-sans text-xs font-semibold tracking-[0.2em] text-indigo uppercase mb-6" data-id="element-2261">
                {isFR ? 'SECTEURS' : 'INDUSTRIES'}
              </motion.div>
              <motion.h2 {...reveal} transition={{
              duration: 0.7,
              delay: 0.1
            }} className="font-serif text-4xl md:text-5xl text-[#121212] mb-6" data-id="element-2262">
                {isFR ? 'Bâtie pour les municipalités.' : 'Built for municipalities.'}
              </motion.h2>
              <motion.p {...reveal} transition={{
              duration: 0.7,
              delay: 0.15
            }} className="font-sans text-xl text-[#121212]/60 max-w-2xl mx-auto" data-id="element-2263">
                {isFR ? "La même architecture s'étend au-delà du municipal." : 'The same architecture extends beyond the municipal world.'}
              </motion.p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2264">
              {INDUSTRIES.map((ind, i) => <motion.div key={ind.title} {...reveal} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} data-id="element-2265">
                  <Link to={ind.to} onClick={() => track('Industry Tile Clicked', {
                industry: ind.title
              })} className={`group h-full border border-[#E4E6EC] p-8 transition-colors flex flex-col justify-between hover:border-indigo/40 ${ind.isInvitation ? 'bg-[#FAFAFB]' : 'bg-white'}`} data-id="element-2266">
                    <div data-id="element-2267">
                      <h3 className={`font-serif text-2xl mb-4 transition-colors ${ind.isInvitation ? 'text-[#121212]/70 group-hover:text-[#121212]' : 'text-[#121212] group-hover:text-indigo'}`} data-id="element-2268">
                        {isFR ? ind.titleFr : ind.title}
                      </h3>
                      <p className="font-sans text-[#121212]/60 mb-8" data-id="element-2269">
                        {isFR ? ind.descFr : ind.desc}
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-2 font-sans text-sm font-medium text-indigo" data-id="element-2270">
                      {ind.isInvitation ? isFR ? 'Parlez-nous' : 'Talk to us' : isFR ? 'Explorer le secteur' : 'Explore Industry'}
                      <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-2271" />
                    </span>
                  </Link>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* SECTION 8: ATNA — signature moment (live assistant lives on /demo + floating launcher) */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-2272">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2273">
            <motion.div {...reveal} className="font-sans text-xs font-semibold tracking-[0.2em] text-indigo uppercase mb-6" data-id="element-2274">
              ATNA
            </motion.div>
            <motion.h2 {...reveal} transition={{
            duration: 0.7,
            delay: 0.05
          }} className="font-serif text-4xl md:text-5xl text-[#121212] mb-6" data-id="element-2275">
              {isFR ? 'Vérification. Confiance. Exécution.' : 'Verify. Trust. Execute.'}
            </motion.h2>
            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-sans text-xl text-[#121212]/70 mb-6 max-w-2xl mx-auto" data-id="element-2276">
              {isFR ? 'Une intelligence vérifiée construite sur une infrastructure de confiance.' : 'Verified intelligence built on trusted infrastructure.'}
            </motion.p>
            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.15
          }} className="font-sans text-lg text-[#121212]/55 mb-10 max-w-2xl mx-auto" data-id="element-2277">
              {isFR ? "ATNA ne remplace pas l'infrastructure. ATNA apprend d'elle." : 'ATNA does not replace infrastructure. ATNA learns from it.'}
            </motion.p>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} data-id="element-2278">
              <Link to="/demo" className="inline-flex items-center gap-2 font-sans text-base font-medium text-indigo hover:text-indigo/80 transition-colors" data-id="element-2279">
                {isFR ? 'Essayer ATNA' : 'Try ATNA'}
                <ArrowRightIcon className="w-4 h-4" data-id="element-2280" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* SECTION 9: FINAL CTA */}
        <section className="py-40 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2281">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2282">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-8" data-id="element-2283">
              {isFR ? 'Prêt à augmenter votre capacité\u202f?' : 'Ready to increase your capacity?'}
            </motion.h2>
            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-sans text-xl text-[#121212]/60 mb-12" data-id="element-2284">
              {isFR ? 'Voyez BCITI en action sur vos propres services municipaux.' : 'See BCITI in action on your own municipal services.'}
            </motion.p>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-2285">
              <Link to="/request-demo" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-sm font-medium uppercase tracking-wider hover:bg-[#4F46E5]/90 transition-colors w-full sm:w-auto" data-id="element-2286">
                {isFR ? 'Demander une démo' : 'Request a Demo'}
                <ArrowRightIcon className="w-4 h-4" data-id="element-2287" />
              </Link>
              <Link to="/demo" className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-[#E4E6EC] text-[#121212] text-sm font-medium uppercase tracking-wider hover:border-[#121212] transition-colors w-full sm:w-auto" data-id="element-2288">
                {isFR ? 'Voir BCITI en action' : 'See BCITI in Action'}
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-2289" />
    </div>;
}